import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Provisional, CtaBand, useList } from "@/components/Blocks";
import { PageHero } from "./About";
import { useSeo } from "@/lib/analytics";
import { fdate } from "@/lib/api";

export const ARTICLE_CATS = ["Desarrollo de software", "Transformación digital", "Cloud computing", "Integraciones empresariales", "Facturación electrónica", "Automatización", "Inteligencia artificial", "Logística tecnológica"];
export const FAQ_CATS = ["Productos", "Licenciamiento", "Suscripciones", "Implementación", "Soporte", "Facturación", "Seguridad"];

export function ResourcesPage() {
  const articles = useList("/public/articles") || [];
  const [cat, setCat] = useState("all");
  useSeo({ title: "Centro de recursos", description: "Artículos, guías y novedades sobre desarrollo de software, cloud, integraciones y facturación electrónica." });
  const shown = articles.filter((a) => cat === "all" || a.category === cat);
  return (
    <>
      <PageHero eyebrow="Recursos" title="Centro de recursos" sub="Guías, artículos y novedades sobre tecnología empresarial." />
      <section className="wrap py-16 md:py-24">
        <div className="mb-8 flex flex-wrap gap-2">
          {["all", ...ARTICLE_CATS].map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`article-cat-${c}`}
              className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${cat === c ? "border-transparent bg-navy text-white" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>{c === "all" ? "Todas" : c}</button>
          ))}
        </div>
        {shown.length ? (
          <div className="grid gap-6 md:grid-cols-3">
            {shown.map((a) => (
              <Link key={a.id} to={`/recursos/${a.slug}`} className="card-cc card-cc-hover group !p-0 overflow-hidden" data-testid={`article-card-${a.slug}`}>
                {a.image_url && <img src={a.image_url} alt="" loading="lazy" className="aspect-[16/9] w-full object-cover" />}
                <div className="p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">{a.category} · {a.type || "Artículo"}</p>
                  <h3 className="mt-2 font-display text-lg font-semibold">{a.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-slate-600">{a.meta_description}</p>
                  <p className="mt-4 text-xs text-slate-400">{a.author} · {fdate(a.date || a.created_at)}</p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <Provisional testid="articles-placeholder" title="Publicaciones en preparación" text="Los artículos, guías, noticias de productos y casos de estudio se gestionan desde el panel administrativo." />
        )}
        <Link to="/preguntas-frecuentes" className="mt-12 inline-flex items-center gap-1 text-sm font-semibold text-brand" data-testid="resources-faq-link">Preguntas frecuentes <ArrowRight className="h-4 w-4" /></Link>
      </section>
    </>
  );
}

export function ArticleDetail() {
  const { slug } = useParams();
  const a = useList(`/public/articles/${slug}`);
  const ok = a && !Array.isArray(a);
  useSeo({
    title: ok ? a.seo_title || a.title : "Recurso", description: ok ? a.meta_description : undefined, image: ok ? a.image_url : undefined,
    jsonLd: ok ? { "@context": "https://schema.org", "@type": "Article", headline: a.title, author: { "@type": "Person", name: a.author }, datePublished: a.date || a.created_at, image: a.image_url } : undefined,
  });
  if (a === null) return <div className="wrap py-32 text-slate-500">Cargando…</div>;
  if (!ok) return <div className="wrap py-32 text-center"><h1 className="text-3xl font-bold">Publicación no encontrada</h1></div>;
  return (
    <article className="wrap max-w-3xl py-16 md:py-24">
      <p className="eyebrow">{a.category}</p>
      <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="article-title">{a.title}</h1>
      <p className="mt-4 text-sm text-slate-500">{a.author} · {fdate(a.date || a.created_at)}</p>
      {a.image_url && <img src={a.image_url} alt="" className="mt-10 w-full rounded-2xl" />}
      <div className="mt-10 space-y-5 text-lg leading-relaxed text-slate-700">{(a.body || "").split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)}</div>
    </article>
  );
}

export function FaqPage() {
  const faqs = useList("/public/faqs") || [];
  useSeo({ title: "Preguntas frecuentes", jsonLd: faqs.length ? { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })) } : undefined });
  const groups = FAQ_CATS.map((c) => [c, faqs.filter((f) => f.category === c)]).filter(([, l]) => l.length);
  return (
    <>
      <PageHero eyebrow="FAQ" title="Preguntas frecuentes" />
      <section className="wrap max-w-3xl py-16 md:py-24">
        {groups.length ? groups.map(([c, list]) => (
          <div key={c} className="mb-10">
            <p className="eyebrow">{c}</p>
            <Accordion type="single" collapsible className="mt-3">
              {list.map((f) => <AccordionItem key={f.id} value={f.id}><AccordionTrigger className="text-left" data-testid={`faq-${f.id}`}>{f.question}</AccordionTrigger><AccordionContent className="text-slate-600">{f.answer}</AccordionContent></AccordionItem>)}
            </Accordion>
          </div>
        )) : <Provisional testid="faq-placeholder" title="Preguntas frecuentes" text={`Categorías previstas: ${FAQ_CATS.join(", ")}. Se administran desde el panel.`} />}
      </section>
      <CtaBand />
    </>
  );
}
